/**
 * Held payouts in the admin (collect and hold, Warren 2026-09-27).
 *
 * Settlement refused a session's split because the Site's electricity
 * reimbursement exceeded its energy revenue. The driver paid in full; the
 * Payout's breakdown then carries the WHOLE charge as platform_fee_cents
 * and 0 to the host and the Site (electricity_cost_cents 0 included) until
 * a human pays them. Read as an ordinary row that says "EVI took
 * everything, the Site is owed nothing", so every surface that shows a
 * payout reads these instead:
 *   - held_electricity_cost_cents: what the Site is owed, paid manually
 *   - breakdown.net_revenue_cents: what EVI actually holds, the charge
 *     less Stripe's fee (platform_fee_cents is the gross)
 *
 * Shared by PayoutsPage and the Reports payout ledger so the two can never
 * disagree about a held row.
 */
import type { Payout } from './api';

type HeldFields = Pick<Payout, 'hold_reason' | 'held_electricity_cost_cents'> & {
  breakdown?: Partial<Payout['breakdown']> | null;
};

/** hold_reason is null (or absent, from an older API) on every ordinary row. */
export function isHeldPayout(p: HeldFields): boolean {
  return typeof p.hold_reason === 'string' && p.hold_reason.length > 0;
}

/** The Site's electricity owed on a held row, paid manually. Null on an
 *  ordinary row, or a held row that predates the held_* fields. */
export function heldElectricityCents(p: HeldFields): number | null {
  if (!isHeldPayout(p)) return null;
  return typeof p.held_electricity_cost_cents === 'number' ? p.held_electricity_cost_cents : null;
}

/** What EVI holds for a held row: the charge less Stripe's fee. Null on an
 *  ordinary row. */
export function heldNetCents(p: HeldFields): number | null {
  if (!isHeldPayout(p)) return null;
  return p.breakdown?.net_revenue_cents ?? 0;
}
